import { LitElement, css, html, nothing } from "lit";
import type { PropertyValues, TemplateResult } from "lit";
import type { Feed, Rule, RuleInput } from "@newsfeed/shared";
import type { ApiClient } from "../api.js";
import { ApiError } from "../api.js";
import { navigate } from "../router.js";
import { sharedStyles } from "../styles.js";
import "../components/rule-form.js";

/** Rule library: list rules and create/edit them. */
export class NfRulesView extends LitElement {
  static override properties = {
    api: { attribute: false },
    rules: { state: true },
    feeds: { state: true },
    editing: { state: true },
    loading: { state: true },
    error: { state: true },
  };

  api!: ApiClient;
  rules: Rule[] = [];
  feeds: Feed[] = [];
  /** null = list view; "new" = create form; Rule = edit form. */
  editing: Rule | "new" | null = null;
  loading = false;
  error = "";

  static override styles = [
    sharedStyles,
    css`
      header {
        display: flex;
        align-items: center;
        margin-bottom: var(--nf-space-12);
      }
      h2 {
        flex: 1;
        font-size: var(--nf-font-18);
      }
      .rule {
        display: flex;
        gap: var(--nf-space-12);
        align-items: flex-start;
        padding: var(--nf-space-12);
        border-bottom: 1px solid var(--nf-border);
      }
      .rule:last-child {
        border-bottom: none;
      }
      .main {
        flex: 1;
        min-width: 0;
      }
      .rule-name {
        font-weight: 600;
      }
      .summary {
        font-size: var(--nf-font-13);
        margin-top: var(--nf-space-2);
      }
      .buttons {
        display: flex;
        gap: var(--nf-space-6);
        flex-wrap: wrap;
        flex-shrink: 0;
      }
      .badge.off {
        background: var(--nf-muted);
      }
    `,
  ];

  override updated(changed: PropertyValues<this>): void {
    if (changed.has("api")) void this.load();
  }

  private async load(): Promise<void> {
    if (!this.api) return;
    this.loading = true;
    this.error = "";
    try {
      const [rules, feeds] = await Promise.all([
        this.api.listRules(),
        this.api.listFeeds(),
      ]);
      this.rules = rules;
      this.feeds = feeds;
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    } finally {
      this.loading = false;
    }
  }

  private async onSave(event: Event): Promise<void> {
    if (!(event instanceof CustomEvent)) return;
    const input = event.detail.input as RuleInput;
    try {
      if (this.editing !== null && this.editing !== "new") {
        await this.api.updateRule(this.editing.id, input);
      } else {
        await this.api.createRule(input);
      }
      await this.load();
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    }
  }

  private async toggleEnabled(rule: Rule): Promise<void> {
    try {
      await this.api.setRuleEnabled(rule.id, !rule.enabled);
      await this.load();
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    }
  }

  private async deleteRule(rule: Rule): Promise<void> {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await this.api.deleteRule(rule.id);
      await this.load();
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    }
  }

  private summarize(rule: Rule): string {
    const scope = rule.watchedFeedUrl
      ? `feed ${rule.watchedFeedUrl}`
      : "all feeds";
    const conds =
      rule.conditions.length === 0
        ? "every article"
        : `${rule.conditionMatch === "match_all" ? "all of" : "any of"} ${rule.conditions.length} condition(s)`;
    const acts = rule.actions.map((a) => a.action).join(", ");
    return `${scope} · ${conds} · → ${acts}`;
  }

  override render(): TemplateResult {
    if (this.editing !== null) {
      return html`
        <header>
          <h2>${this.editing === "new" ? "New rule" : "Edit rule"}</h2>
        </header>
        <div class="card">
          <nf-rule-form
            .feeds=${this.feeds}
            .rule=${this.editing === "new" ? null : this.editing}
            @rule-save=${(e: Event) => void this.onSave(e)}
            @rule-cancel=${() => {
              this.editing = null;
            }}
          ></nf-rule-form>
        </div>
      `;
    }

    return html`
      <header>
        <h2>Rules</h2>
        <button
          class="primary"
          @click=${() => {
            this.editing = "new";
          }}
        >
          New rule
        </button>
      </header>
      ${this.error ? html`<div class="card error">${this.error}</div>` : nothing}
      ${
        this.loading
          ? html`<div class="empty">Loading…</div>`
          : this.rules.length === 0
            ? html`<div class="empty">No rules yet.</div>`
            : html`<div class="card" style="padding:0">
                ${this.rules.map(
                  (rule) =>
                    html`<div class="rule">
                    <div class="main">
                      <div class="rule-name">
                        ${rule.name}
                        <span class="badge ${rule.enabled ? "" : "off"}"
                          >${rule.enabled ? "on" : "off"}</span
                        >
                      </div>
                      <div class="summary muted">${this.summarize(rule)}</div>
                    </div>
                    <div class="buttons">
                      <button
                        @click=${() => navigate(`/rules/${rule.id}/activity`)}
                      >
                        Activity
                      </button>
                      <button @click=${() => void this.toggleEnabled(rule)}>
                        ${rule.enabled ? "Disable" : "Enable"}
                      </button>
                      <button
                        @click=${() => {
                          this.editing = rule;
                        }}
                      >
                        Edit
                      </button>
                      <button
                        class="danger"
                        @click=${() => void this.deleteRule(rule)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>`,
                )}
              </div>`
      }
    `;
  }
}

customElements.define("nf-rules-view", NfRulesView);

declare global {
  interface HTMLElementTagNameMap {
    "nf-rules-view": NfRulesView;
  }
}
